import { useEffect, useMemo, useState } from "react";
import { ArrowUpRight, Bell, CheckCircle2, Loader2, RotateCcw, Search } from "lucide-react";
import { Reveal } from "@/components/landing/shared";
import { SaveJobButton } from "@/components/candidate/SaveJobButton";

const API = process.env.REACT_APP_BACKEND_URL;

const SALARY_OPTIONS = [
    { value: "", label: "Any salary" },
    { value: "0-1500", label: "Up to $1,500 / month" },
    { value: "1500-2500", label: "$1,500 – $2,500 / month" },
    { value: "2500-4000", label: "$2,500 – $4,000 / month" },
    { value: "4000-", label: "$4,000+ / month" },
];

const POSTED_OPTIONS = [
    { value: "", label: "Any time" },
    { value: "7", label: "Last 7 days" },
    { value: "30", label: "Last 30 days" },
];

const SORT_OPTIONS = [
    { value: "", label: "Newest first" },
    { value: "relevance", label: "Most relevant" },
    { value: "salary_high", label: "Salary: high to low" },
    { value: "salary_low", label: "Salary: low to high" },
];

const EMPTY_FILTERS = {
    q: "", country: "", city: "", category: "", employment_type: "", experience: "",
    salary: "", accommodation: "", transportation: "", posted_within: "", sort: "",
};

const selectClass =
    "w-full cursor-pointer border border-ink/15 bg-ivory px-4 py-3 text-sm text-ink outline-none transition-colors focus:border-forest";

const formatDate = (iso) =>
    iso
        ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
        : null;

const JobAlerts = ({ meta }) => {
    const [status, setStatus] = useState("idle");

    const subscribe = async (e) => {
        e.preventDefault();
        if (status === "submitting") return;
        setStatus("submitting");
        try {
            const res = await fetch(`${API}/api/job-alerts`, {
                method: "POST",
                body: new FormData(e.target),
            });
            if (!res.ok) throw new Error();
            setStatus("success");
        } catch {
            setStatus("error");
        }
    };

    return (
        <section data-testid="job-alerts-section" className="bg-night py-16 text-ivory lg:py-20">
            <div className="mx-auto max-w-7xl px-6 lg:px-10">
                <div className="grid gap-10 lg:grid-cols-12 lg:items-center">
                    <div className="lg:col-span-5">
                        <p className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.3em] text-champagne">
                            <span className="h-px w-8 bg-champagne" aria-hidden="true" />
                            Get Job Alerts
                        </p>
                        <h2 className="mt-4 font-serif text-2xl font-medium tracking-tight sm:text-3xl">
                            New roles, straight to your inbox
                        </h2>
                        <p className="mt-4 max-w-md text-sm leading-relaxed text-ivory/65">
                            Tell us your profession and preferences — when a matching international role is
                            published, you'll be the first to know.
                        </p>
                    </div>
                    <div className="lg:col-span-7">
                        {status === "success" ? (
                            <div className="flex items-center gap-4 border border-ivory/15 p-6" data-testid="job-alerts-success">
                                <CheckCircle2 size={26} strokeWidth={1.25} className="shrink-0 text-champagne" />
                                <p className="text-sm leading-relaxed text-ivory/80">
                                    You're subscribed. We'll email you when a matching role is published.
                                </p>
                            </div>
                        ) : (
                            <form data-testid="job-alerts-form" onSubmit={subscribe} className="grid gap-3 sm:grid-cols-2">
                                <input type="text" name="fax" tabIndex={-1} autoComplete="off" aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 opacity-0" />
                                <input
                                    type="email" name="email" required data-testid="job-alerts-email"
                                    placeholder="Your email address"
                                    className="border border-ivory/20 bg-night/40 px-4 py-3.5 text-sm text-ivory outline-none transition-colors placeholder:text-ivory/35 focus:border-champagne sm:col-span-2"
                                />
                                <input
                                    type="text" name="profession" data-testid="job-alerts-profession"
                                    placeholder="Profession or keyword (e.g. nurse)"
                                    className="border border-ivory/20 bg-night/40 px-4 py-3.5 text-sm text-ivory outline-none transition-colors placeholder:text-ivory/35 focus:border-champagne"
                                />
                                <select name="country" data-testid="job-alerts-country" className="cursor-pointer border border-ivory/20 bg-night px-4 py-3.5 text-sm text-ivory outline-none focus:border-champagne">
                                    <option value="">Any country</option>
                                    {meta.countries.map((c) => <option key={c} value={c}>{c}</option>)}
                                </select>
                                <select name="industry" data-testid="job-alerts-industry" className="cursor-pointer border border-ivory/20 bg-night px-4 py-3.5 text-sm text-ivory outline-none focus:border-champagne">
                                    <option value="">Any industry</option>
                                    {meta.categories.map((c) => <option key={c} value={c}>{c}</option>)}
                                </select>
                                <button
                                    type="submit" data-testid="job-alerts-submit" disabled={status === "submitting"}
                                    className="inline-flex items-center justify-center gap-2 bg-ivory px-6 py-3.5 text-xs font-semibold uppercase tracking-[0.14em] text-ink transition-colors hover:bg-champagne disabled:opacity-70"
                                >
                                    {status === "submitting" ? <Loader2 size={15} className="animate-spin" /> : <Bell size={15} strokeWidth={1.5} />}
                                    Subscribe
                                </button>
                                {status === "error" && (
                                    <p data-testid="job-alerts-error" className="text-xs text-ivory/60 sm:col-span-2">
                                        Subscription failed — please try again.
                                    </p>
                                )}
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default function JobsPage() {
    const [jobs, setJobs] = useState(null);
    const [meta, setMeta] = useState({ countries: [], cities: [], categories: [], employment_types: [], experiences: [] });
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [error, setError] = useState("");

    useEffect(() => {
        document.title = "International Jobs & Overseas Recruitment | Afrinexa Global";
        const metaDesc = document.querySelector('meta[name="description"]');
        const previous = metaDesc?.getAttribute("content");
        metaDesc?.setAttribute(
            "content",
            "Find international jobs and overseas employment through Afrinexa Global — global recruitment across healthcare, construction, engineering, hospitality and more, with structured screening and mobility support."
        );
        return () => {
            document.title = "Afrinexa Global | International Recruitment & Global Mobility";
            if (metaDesc && previous) metaDesc.setAttribute("content", previous);
        };
    }, []);

    useEffect(() => {
        fetch(`${API}/api/jobs/meta`)
            .then((r) => (r.ok ? r.json() : null))
            .then((d) => d && setMeta(d))
            .catch(() => {});
    }, []);

    useEffect(() => {
        const t = setTimeout(async () => {
            try {
                const params = new URLSearchParams();
                ["q", "country", "city", "category", "employment_type", "experience", "accommodation", "transportation", "posted_within", "sort"].forEach((k) => {
                    if (filters[k]) params.set(k, filters[k]);
                });
                if (filters.salary) {
                    const [lo, hi] = filters.salary.split("-");
                    if (lo && Number(lo) > 0) params.set("salary_min", lo);
                    if (hi) params.set("salary_max", hi);
                }
                const res = await fetch(`${API}/api/jobs?${params}`);
                if (!res.ok) throw new Error();
                setJobs(await res.json());
                setError("");
            } catch {
                setError("Jobs could not be loaded right now. Please try again shortly.");
                setJobs([]);
            }
        }, 250);
        return () => clearTimeout(t);
    }, [filters]);

    const activeCount = useMemo(
        () => Object.entries(filters).filter(([k, v]) => v && k !== "sort").length,
        [filters]
    );

    const set = (key) => (e) => setFilters((f) => ({ ...f, [key]: e.target.value }));

    return (
        <div data-testid="jobs-page">
            <section className="bg-night pb-16 pt-40 text-ivory lg:pb-20 lg:pt-48">
                <div className="mx-auto max-w-7xl px-6 lg:px-10">
                    <p className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.32em] text-champagne">
                        <span className="h-px w-8 bg-champagne" aria-hidden="true" />
                        Afrinexa Global • International Careers
                    </p>
                    <h1 className="mt-6 max-w-3xl font-serif text-4xl font-medium leading-[1.08] tracking-tight sm:text-5xl">
                        Find International Opportunities
                    </h1>
                    <p className="mt-6 max-w-xl text-base leading-relaxed text-ivory/70">
                        Current openings from employers recruiting through Afrinexa Global — with
                        structured screening, documentation and mobility support.
                    </p>
                </div>
            </section>

            <section className="border-b border-ink/10 bg-sand py-8">
                <div className="mx-auto max-w-7xl px-6 lg:px-10">
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                        <div className="relative sm:col-span-2">
                            <Search size={16} strokeWidth={1.5} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink/35" />
                            <input
                                data-testid="jobs-search-input"
                                type="text"
                                value={filters.q}
                                onChange={set("q")}
                                placeholder="Job title, keyword, skill or profession…"
                                className="w-full border border-ink/15 bg-ivory py-3 pl-11 pr-4 text-sm outline-none transition-colors placeholder:text-ink/35 focus:border-forest"
                            />
                        </div>
                        <select data-testid="jobs-filter-country" value={filters.country} onChange={set("country")} className={selectClass}>
                            <option value="">All countries</option>
                            {meta.countries.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                        <select data-testid="jobs-filter-city" value={filters.city} onChange={set("city")} className={selectClass}>
                            <option value="">All cities</option>
                            {meta.cities.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                        <select data-testid="jobs-filter-category" value={filters.category} onChange={set("category")} className={selectClass}>
                            <option value="">All industries</option>
                            {meta.categories.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                        <select data-testid="jobs-filter-type" value={filters.employment_type} onChange={set("employment_type")} className={selectClass}>
                            <option value="">Any employment type</option>
                            {meta.employment_types.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                        <select data-testid="jobs-filter-experience" value={filters.experience} onChange={set("experience")} className={selectClass}>
                            <option value="">Any experience level</option>
                            {meta.experiences.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                        <select data-testid="jobs-filter-salary" value={filters.salary} onChange={set("salary")} className={selectClass}>
                            {SALARY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                        <select data-testid="jobs-filter-accommodation" value={filters.accommodation} onChange={set("accommodation")} className={selectClass}>
                            <option value="">Accommodation — any</option>
                            <option value="Yes">Accommodation provided</option>
                        </select>
                        <select data-testid="jobs-filter-transportation" value={filters.transportation} onChange={set("transportation")} className={selectClass}>
                            <option value="">Transportation — any</option>
                            <option value="Yes">Transportation provided</option>
                        </select>
                        <select data-testid="jobs-filter-posted" value={filters.posted_within} onChange={set("posted_within")} className={selectClass}>
                            {POSTED_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                        <select data-testid="jobs-sort" value={filters.sort} onChange={set("sort")} className={selectClass}>
                            {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                        <button
                            type="button"
                            data-testid="jobs-clear-filters"
                            onClick={() => setFilters(EMPTY_FILTERS)}
                            disabled={activeCount === 0}
                            className="inline-flex items-center justify-center gap-2 border border-ink/15 px-4 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-ink/60 transition-colors hover:border-forest hover:text-forest disabled:cursor-not-allowed disabled:opacity-40"
                        >
                            <RotateCcw size={14} strokeWidth={1.5} />
                            Clear{activeCount > 0 ? ` (${activeCount})` : ""}
                        </button>
                    </div>
                </div>
            </section>

            <section className="bg-ivory py-16 lg:py-20">
                <div className="mx-auto max-w-7xl px-6 lg:px-10">
                    {jobs === null ? (
                        <div className="flex justify-center py-20">
                            <Loader2 size={22} className="animate-spin text-forest" />
                        </div>
                    ) : error ? (
                        <p data-testid="jobs-error" className="border-t border-ink/10 py-16 text-center text-sm text-ink/55">{error}</p>
                    ) : jobs.length === 0 ? (
                        <p data-testid="jobs-empty" className="border-t border-ink/10 py-16 text-center text-sm text-ink/55">
                            No open roles match these filters right now. Clear the filters or subscribe to
                            job alerts below.
                        </p>
                    ) : (
                        <Reveal>
                            <p className="text-xs font-medium uppercase tracking-[0.22em] text-ink/45" data-testid="jobs-count">
                                {jobs.length} open role{jobs.length === 1 ? "" : "s"}
                            </p>
                            <div className="mt-6" data-testid="jobs-list">
                                {jobs.map((job) => (
                                    <div
                                        key={job.id}
                                        data-testid={`job-row-${job.slug}`}
                                        className="grid grid-cols-1 gap-5 border-t border-ink/10 px-2 py-7 transition-colors hover:bg-sand lg:grid-cols-[1fr_auto] lg:items-center"
                                    >
                                        <div className="min-w-0">
                                            <div className="flex flex-wrap items-center gap-3">
                                                <h2 className="font-serif text-xl font-medium tracking-tight text-ink sm:text-2xl">
                                                    {job.title}
                                                </h2>
                                                {job.is_demo ? (
                                                    <span className="border border-ink/15 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.18em] text-ink/40">
                                                        Demo listing
                                                    </span>
                                                ) : null}
                                            </div>
                                            <p className="mt-1.5 text-sm text-ink/55">
                                                {job.company} · {job.city ? `${job.city}, ` : ""}{job.country}
                                                {job.category ? ` · ${job.category}` : ""}
                                            </p>
                                            <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-xs text-ink/50">
                                                {job.employment_type && <span>{job.employment_type}</span>}
                                                {job.salary_text && <span className="font-medium text-ink/65">{job.salary_text}</span>}
                                                {job.experience && <span>{job.experience}</span>}
                                                {job.accommodation === "Yes" && <span className="border border-forest/25 px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.12em] text-forest">Accommodation</span>}
                                                {job.transportation === "Yes" && <span className="border border-forest/25 px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.12em] text-forest">Transportation</span>}
                                                {job.posted_at && <span>Posted {formatDate(job.posted_at)}</span>}
                                                {job.deadline && <span>Apply by {formatDate(job.deadline)}</span>}
                                            </div>
                                        </div>
                                        <div className="flex flex-col gap-2.5 sm:flex-row lg:flex-col">
                                            <SaveJobButton jobId={job.id} />
                                            <a
                                                href={`/jobs/${job.slug}`}
                                                data-testid={`job-view-${job.slug}`}
                                                className="inline-flex items-center justify-center gap-2 border border-ink/20 px-6 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-ink transition-colors hover:border-forest hover:text-forest"
                                            >
                                                View Job
                                                <ArrowUpRight size={14} strokeWidth={1.5} />
                                            </a>
                                            <a
                                                href={`/jobs/${job.slug}#apply`}
                                                data-testid={`job-apply-${job.slug}`}
                                                className="inline-flex items-center justify-center gap-2 bg-forest px-6 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-ivory transition-colors hover:bg-forest-deep"
                                            >
                                                Apply Now
                                            </a>
                                        </div>
                                    </div>
                                ))}
                                <div className="border-t border-ink/10" />
                            </div>
                            <p className="mt-8 text-xs leading-relaxed text-ink/45">
                                Afrinexa Global supports recruitment and mobility processes. Employment and
                                visa decisions rest with employers and the relevant authorities.
                            </p>
                        </Reveal>
                    )}
                </div>
            </section>

            <JobAlerts meta={meta} />
        </div>
    );
}
